class TimelineVSO {
  constructor(container) {
    this.container = container;
    this.progressLine = null;
    this.progressFill = null;
    this.timelineSteps = [];
    this.stepElements = [];
    this.observer = null;
    this.isInViewport = false;
    this.videoContainer = null;
    this.videoBackground = null;
    this.playButton = null;
    this.muteButton = null;
    this.videoElement = null;
    this.vimeoPlayer = null;
    this.videoType = null;
    this.videoEventsSetup = false;
    this.isPlaying = false;
    this.isMuted = false;
    this.init();
  }

  init() {
    this.setupElements();
    this.setupVideo();
    this.setupObserver();
    this.bindEvents();
  }

  setupElements() {
    const isMobile = window.innerWidth <= 990;
    
    this.progressLine = this.container.querySelector('.timeline_vso__timeline-line');
    this.progressFill = this.container.querySelector('.timeline_vso__progress-fill');
    this.stepElements = Array.from(this.container.querySelectorAll('.timeline_vso__step'));
    this.timelineSteps = this.stepElements;
    this.calculateProgressLineHeight();
    
  }

  calculateProgressLineHeight() {
    if (!this.progressLine || this.timelineSteps.length === 0) return;
    
    const timelineContainer = this.container.querySelector('.timeline_vso__timeline-steps');
    if (!timelineContainer) return;
    
    const firstStep = this.timelineSteps[0];
    const lastStep = this.timelineSteps[this.timelineSteps.length - 1];
    
    if (!firstStep || !lastStep) return;
    
    const firstStepOffsetTop = firstStep.offsetTop;
    const lastStepOffsetTop = lastStep.offsetTop;
    
    const iconCenterOffset = 14;
    
    const calculatedHeight = lastStepOffsetTop - firstStepOffsetTop;
    
    this.progressLine.style.height = `${calculatedHeight}px`;
  }

  setupVideo() {
    this.videoContainer = this.container.querySelector('.timeline_vso__video-container');
    this.videoBackground = this.container.querySelector('.timeline_vso__video-background');
    this.playButton = this.container.querySelector('.timeline_vso__video-btn--play');
    this.videoType = this.videoContainer?.dataset.videoType || 'mp4';

    if (this.videoType === 'vimeo') {
      this.setupVimeoVideo();
    } else {
      this.setupMP4Video();
    }

    this.addMuteButton();
    this.setupBasicVideoEvents();
  }

  setupMP4Video() {
    this.videoElement = document.createElement('video');
    this.videoElement.style.display = 'none';
    this.videoElement.muted = true;
    this.videoElement.loop = true;
    this.videoElement.playsInline = true;

    const videoUrl = this.container.dataset.videoUrl;
    if (videoUrl) {
      this.videoElement.src = videoUrl;
    }

    if (this.videoContainer) {
      this.videoContainer.appendChild(this.videoElement);
    }
  }

  setupVimeoVideo() {
    const vimeoId = this.container.dataset.vimeoId;
    if (!vimeoId) return;

    if (this.videoContainer) {
      const existingIframes = this.videoContainer.querySelectorAll('.timeline_vso__vimeo-player');
      existingIframes.forEach(iframe => iframe.remove());
    }

    const orphanIframes = this.container.querySelectorAll('iframe:not(.timeline_vso__vimeo-player)');
    orphanIframes.forEach(iframe => {
      if (iframe.src && iframe.src.includes('vimeo.com')) {
        iframe.remove();
      }
    });

    const iframe = document.createElement('iframe');
    iframe.setAttribute('data-src', `https://player.vimeo.com/video/${vimeoId}?autopause=0&autoplay=0&background=0&byline=0&controls=0&dnt=1&loop=1&muted=0&portrait=0&title=0&texttrack=0&transparent=1`);
    iframe.className = 'timeline_vso__vimeo-player';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.style.display = 'none';

    if (this.videoContainer) {
      this.videoContainer.appendChild(iframe);
    }

    this.setupVimeoLazyLoad(iframe);
    this.setupVimeoOrphanCleaner();
  }

  setupVimeoOrphanCleaner() {
    if (this.vimeoOrphanObserver) return;

    this.vimeoOrphanObserver = new MutationObserver((mutations) => {
      const orphanIframes = this.container.querySelectorAll('iframe:not(.timeline_vso__vimeo-player)');
      orphanIframes.forEach(iframe => {
        if (iframe.src && iframe.src.includes('vimeo.com')) {
          iframe.remove();
        }
      });
    });

    this.vimeoOrphanObserver.observe(this.container, {
      childList: true,
      subtree: true
    });
  }

  setupVimeoLazyLoad(iframe) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && iframe.dataset.src) {
          iframe.src = iframe.dataset.src;
          iframe.removeAttribute('data-src');

          this.waitForVimeoAPI(() => {
            this.vimeoPlayer = new Vimeo.Player(iframe);
            this.vimeoPlayer.ready().then(() => {
              this.vimeoPlayer.setVolume(1);
              this.vimeoPlayer.setLoop(true);
              this.setupVimeoPlayerEvents();
            });
          });

          observer.unobserve(this.videoContainer);
        }
      });
    }, {
      rootMargin: '200px',
      threshold: 0.01
    });

    observer.observe(this.videoContainer);
  }

  waitForVimeoAPI(callback) {
    if (typeof Vimeo !== 'undefined') {
      callback();
    } else {
      setTimeout(() => this.waitForVimeoAPI(callback), 100);
    }
  }

  setupBasicVideoEvents() {
    if (this.videoEventsSetup) return;
    this.videoEventsSetup = true;

    if (this.playButton && !this.playButton.dataset.listenerAdded) {
      this.playButton.dataset.listenerAdded = 'true';
      this.playButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleVideo();
      });
    }

    if (this.muteButton && !this.muteButton.dataset.listenerAdded) {
      this.muteButton.dataset.listenerAdded = 'true';
      this.muteButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleMute();
      });
    }

    if (this.videoBackground && !this.videoBackground.dataset.listenerAdded) {
      this.videoBackground.dataset.listenerAdded = 'true';
      this.videoBackground.addEventListener('click', () => this.toggleVideo());
    }

    if (this.videoContainer && !this.videoContainer.dataset.listenerAdded) {
      this.videoContainer.dataset.listenerAdded = 'true';
      this.videoContainer.addEventListener('click', (e) => {
        if (e.target === this.videoContainer ||
            e.target === this.videoBackground ||
            e.target === this.videoElement ||
            (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'SVG' && e.target.tagName !== 'PATH')) {
          this.toggleVideo();
        }
      });
    }

    if (this.videoType === 'mp4') {
      this.setupMP4PlayerEvents();
    }
  }

  setupMP4PlayerEvents() {
    if (!this.videoElement) return;

    this.videoElement.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleVideo();
    });

    this.videoElement.addEventListener('play', () => {
      this.isPlaying = true;
      this.updatePlayButton();
      if (this.videoContainer) {
        this.videoContainer.classList.add('timeline_vso__video-container--playing');
      }
    });

    this.videoElement.addEventListener('pause', () => {
      this.isPlaying = false;
      this.updatePlayButton();
      if (this.videoContainer) {
        this.videoContainer.classList.remove('timeline_vso__video-container--playing');
      }
    });
  }

  setupVimeoPlayerEvents() {
    if (!this.vimeoPlayer) return;

    this.vimeoPlayer.on('play', () => {
      this.isPlaying = true;
      this.updatePlayButton();
      if (this.videoContainer) {
        this.videoContainer.classList.add('timeline_vso__video-container--playing');
      }
    });

    this.vimeoPlayer.on('pause', () => {
      this.isPlaying = false;
      this.updatePlayButton();
      if (this.videoContainer) {
        this.videoContainer.classList.remove('timeline_vso__video-container--playing');
      }
    });
  }

  addMuteButton() {
    if (!this.videoContainer) return;
    
    const existingMuteButton = this.videoContainer.querySelector('.timeline_vso__video-btn--mute');
    if (existingMuteButton) {
      this.muteButton = existingMuteButton;
      return;
    }
    
    this.muteButton = document.createElement('button');
    this.muteButton.className = 'timeline_vso__video-btn timeline_vso__video-btn--mute';
    this.muteButton.type = 'button';
    this.muteButton.innerHTML = `
      <svg width="18" height="15" viewBox="0 0 18 15" fill="none" class="timeline_vso__mute-icon">
        <path d="M11 1L6 4H2V11H6L11 14V1Z" stroke="currentColor" stroke-width="2" fill="none"/>
        <path d="M15.54 4.46L13.46 6.54M13.46 4.46L15.54 6.54" stroke="currentColor" stroke-width="2" class="timeline_vso__mute-cross"/>
      </svg>
    `;
    
    const controls = this.videoContainer.querySelector('.timeline_vso__video-controls');
    if (controls) {
      controls.appendChild(this.muteButton);
    }
  }

  setupObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.isInViewport = entry.isIntersecting;
        if (this.isInViewport) {
          this.updateProgress();
        }
      });
    }, {
      root: null,
      rootMargin: '-20% 0px -20% 0px',
      threshold: 0
    });

    this.observer.observe(this.container);
  }

  updateProgress() {
    if (!this.progressFill || this.timelineSteps.length === 0 || !this.isInViewport) return;
    
    const timelineContainer = this.container.querySelector('.timeline_vso__timeline-steps');
    if (!timelineContainer) return;

    const firstStep = this.timelineSteps[0];
    const lastStep = this.timelineSteps[this.timelineSteps.length - 1];
    
    if (!firstStep || !lastStep) return;

    const viewportHeight = window.innerHeight;
    const viewportCenter = viewportHeight / 2;
    const progressTrigger = viewportCenter;
    const firstStepRect = firstStep.getBoundingClientRect();
    const firstStepTop = firstStepRect.top;
    const lastStepRect = lastStep.getBoundingClientRect();
    const lastStepTop = lastStepRect.top;
    
    let scrollProgress = 0;
    
    if (firstStepTop <= progressTrigger && lastStepTop >= progressTrigger) {
      const totalDistance = firstStepTop - lastStepTop;
      const currentDistance = firstStepTop - progressTrigger;
      scrollProgress = Math.max(0, Math.min(1, currentDistance / totalDistance));
    } else if (firstStepTop > progressTrigger) {
      scrollProgress = 0;
    } else if (lastStepTop < progressTrigger) {
      scrollProgress = 1;
    }
    
    const progressPercent = scrollProgress * 100;
    this.progressFill.style.height = `${progressPercent}%`;
    this.updateStepsStates(scrollProgress);
  }

  updateStepsStates(scrollProgress) {
    const stepsCount = this.timelineSteps.length;
    if (stepsCount === 0) return;
    
    const progressStep = 1 / stepsCount;
    const activeStepsCount = Math.floor(scrollProgress / progressStep) + (scrollProgress > 0 ? 1 : 0);
    
    this.timelineSteps.forEach((step, index) => {
      const isActive = index < activeStepsCount;
      step.classList.toggle('timeline_vso__step--active', isActive);
    });
  }

  toggleVideo() {
    if (this.isPlaying) {
      this.pauseVideo();
    } else {
      this.playVideo();
    }
  }

  playVideo() {
    if (this.videoType === 'vimeo') {
      const iframe = this.videoContainer?.querySelector('.timeline_vso__vimeo-player');
      if (!iframe) return;

      if (!this.vimeoPlayer && iframe.dataset.src) {
        iframe.src = iframe.dataset.src;
        iframe.removeAttribute('data-src');

        this.waitForVimeoAPI(() => {
          this.vimeoPlayer = new Vimeo.Player(iframe);
          this.vimeoPlayer.ready().then(() => {
            this.vimeoPlayer.setVolume(1);
            this.vimeoPlayer.setLoop(true);
            this.setupVimeoPlayerEvents();
            iframe.style.display = 'block';
            this.vimeoPlayer.play().then(() => {
              this.isPlaying = true;
              this.updatePlayButton();
              if (this.videoContainer) {
                this.videoContainer.classList.add('timeline_vso__video-container--playing');
              }
            });
          });
        });
      } else if (this.vimeoPlayer) {
        iframe.style.display = 'block';
        this.vimeoPlayer.play().then(() => {
          this.isPlaying = true;
          this.updatePlayButton();
          if (this.videoContainer) {
            this.videoContainer.classList.add('timeline_vso__video-container--playing');
          }
        });
      }
    } else if (this.videoElement) {
      this.videoElement.style.display = 'block';
      this.videoElement.play();
    }
  }

  pauseVideo() {
    if (this.videoType === 'vimeo' && this.vimeoPlayer) {
      this.vimeoPlayer.pause().then(() => {
        this.isPlaying = false;
        this.updatePlayButton();
        if (this.videoContainer) {
          this.videoContainer.classList.remove('timeline_vso__video-container--playing');
        }
      });
    } else if (this.videoElement) {
      this.videoElement.pause();
    }
  }

  toggleMute() {
    this.isMuted = !this.isMuted;

    if (this.videoType === 'vimeo' && this.vimeoPlayer) {
      this.vimeoPlayer.setVolume(this.isMuted ? 0 : 1);
    } else if (this.videoElement) {
      this.videoElement.muted = this.isMuted;
    }

    this.updateMuteButton();
  }

  updatePlayButton() {
    if (!this.playButton) return;
    
    const svg = this.playButton.querySelector('svg');
    if (!svg) return;
    
    if (this.isPlaying) {
      svg.innerHTML = '<rect x="4" y="2" width="2" height="10" fill="currentColor"/><rect x="10" y="2" width="2" height="10" fill="currentColor"/>';
    } else {
      svg.innerHTML = '<path d="M13.5 7L1.5 0.5V13.5L13.5 7Z" fill="currentColor"/>';
    }
  }

  updateMuteButton() {
    if (!this.muteButton) return;

    const muteCross = this.muteButton.querySelector('.timeline_vso__mute-cross');
    if (!muteCross) return;

    if (this.isMuted) {
      muteCross.style.display = 'none';
    } else {
      muteCross.style.display = 'block';
    }
  }

  bindEvents() {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateProgress();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.setupElements();
        if (this.isInViewport) {
          if (this.progressFill) {
            this.progressFill.style.height = '0%';
          }
          requestAnimationFrame(() => {
            this.calculateProgressLineHeight();
            this.updateProgress();
          });
        }
      }, 150);
    });

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.contains(this.container)) {
        this.destroy();
        setTimeout(() => this.init(), 100);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(this.container)) {
        this.destroy();
      }
    });
    
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.container.contains(event.target)) return;
      
      const blockElement = event.target.closest('.timeline_vso__step');
      if (blockElement) {
        const stepIndex = Array.from(this.timelineSteps).indexOf(blockElement);
        if (stepIndex !== -1) {
          this.scrollToStep(stepIndex);
        }
      }
    });
  }

  scrollToStep(index) {
    if (index >= 0 && index < this.timelineSteps.length) {
      const step = this.timelineSteps[index];
      const offsetTop = step.offsetTop + this.container.offsetTop - (window.innerHeight / 2);
      
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.vimeoOrphanObserver) {
      this.vimeoOrphanObserver.disconnect();
      this.vimeoOrphanObserver = null;
    }

    if (this.vimeoPlayer) {
      this.vimeoPlayer.destroy();
      this.vimeoPlayer = null;
    }

    const vimeoIframe = this.videoContainer?.querySelector('.timeline_vso__vimeo-player');
    if (vimeoIframe) {
      vimeoIframe.remove();
    }

    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.remove();
      this.videoElement = null;
    }

    if (this.muteButton && this.muteButton.parentNode) {
      this.muteButton.remove();
      this.muteButton = null;
    }
  }
}

function initTimelineVSO() {
  const timelineContainers = document.querySelectorAll('[data-section-type="timeline_vso"]');
  
  timelineContainers.forEach((container, index) => {
    
    if (container.timelineVSOInstance) {
      container.timelineVSOInstance.destroy();
      container.timelineVSOInstance = null;
    }
    
    container.timelineVSOInstance = new TimelineVSO(container);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTimelineVSO);
} else {
  initTimelineVSO();
}

document.addEventListener('shopify:section:load', (event) => {
  const container = event.target.querySelector('[data-section-type="timeline_vso"]');
  if (container && !container.timelineVSOInstance) {
    container.timelineVSOInstance = new TimelineVSO(container);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const container = event.target.querySelector('[data-section-type="timeline_vso"]');
  if (container && container.timelineVSOInstance) {
    container.timelineVSOInstance.destroy();
    container.timelineVSOInstance = null;
  }
});